/**
 * Formats a currency amount received from the backend in cents to a string in dollars with two decimal places and thousands separators.
 *
 * The backend always provides currency values in cents (e.g., 12345 for $123.45).
 * This function converts the cents to dollars and formats the result for display.
 *
 * @param cents - The currency amount in cents from the backend.
 * @param locale - Optional. The locale string for formatting (default is 'en-US').
 * @returns The formatted currency string in dollars (e.g., "1,234.56").
 */
export function formatCentsToCurrency(cents: number, locale: string = 'en-US'): string {
  return (cents / 100).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function formatCentsToCurrencyNoDecimals(cents: number, locale: string = 'en-US'): string {
  return (cents / 100).toLocaleString(locale);
}

export function formatCentsToCurrencyNumber(cents: number): number {
  return (cents / 100);
}

export function formatCurrencyToCents(amount: number): number {
  return Math.round(amount * 100);
}

/**
 * Formats a game multiplier for display, rounding to a fixed number of decimals
 * and trimming trailing zeros so long floating-point values don't overflow the UI.
 * - 0.35714285714285715 -> "0.36"
 * - 2 -> "2"
 * - 1.5 -> "1.5"
 *
 * @param multiplier - The raw multiplier value from the backend.
 * @param decimals - Optional. Maximum number of decimal places (default is 2).
 * @returns The formatted multiplier string.
 */
export function formatMultiplier(multiplier: number, decimals: number = 2): string {
  if (!Number.isFinite(multiplier)) {
    return '0';
  }

  const factor = 10 ** Math.max(0, decimals);
  const rounded = Math.round(multiplier * factor) / factor;

  return decimals > 0
    ? rounded.toFixed(decimals).replace(/0+$/, '').replace(/\.$/, '')
    : String(rounded);
}

type FormatCompactNumberOptions = {
  scale?: number;
  suffixes?: {
    thousand: string;
    million: string;
    billion: string;
  };
  decimals?: number;
  /**
   * Smallest unit to compact. Defaults to `'thousand'`.
   * When set to `'million'`, values below 1M are shown in full (with thousands
   * separators) and only millions/billions are compacted.
   */
  minUnit?: 'thousand' | 'million';
};

/**
 * Formats big numbers to compact form:
 * - 999 -> "999"
 * - 10_000 -> "10к"
 * - 10500 -> "10.5к" (with decimals=1)
 * - 1_200_000 -> "1.2м"
 *
 * With `minUnit: 'million'` thousands are not compacted:
 * - 250_000 -> "250,000"
 * - 1_200_000 -> "1.2M"
 */
export function formatCompactNumber(value: number, options: FormatCompactNumberOptions = {}): string {
  const {
    suffixes = { thousand: 'K', million: 'M', billion: 'B' },
    decimals = 1,
    scale = 1,
    minUnit = 'thousand',
  } = options;

  if (!Number.isFinite(value)) {
    return '0';
  }

  const normalized = scale !== 0 ? value / scale : value;
  const sign = normalized < 0 ? '-' : '';
  const abs = Math.abs(normalized);

  const round = (n: number) => {
    const factor = 10 ** Math.max(0, decimals);
    return Math.round(n * factor) / factor;
  };

  const format = (n: number, suffix: string) => {
    const rounded = round(n);
    const asString = decimals > 0 ? rounded.toFixed(decimals) : String(rounded);

    // Trim trailing zeros safely (no catastrophic backtracking).
    // Examples: "10.0" -> "10", "10.50" -> "10.5"
    let trimmed = asString;
    if (decimals > 0 && trimmed.includes('.')) {
      trimmed = trimmed.replace(/0+$/, '').replace(/\.$/, '');
    }

    return `${sign}${trimmed}${suffix}`;
  };

  if (abs >= 1_000_000_000) {
    return format(abs / 1_000_000_000, suffixes.billion);
  }
  if (abs >= 1_000_000) {
    return format(abs / 1_000_000, suffixes.million);
  }
  if (minUnit !== 'million' && abs >= 1_000) {
    return format(abs / 1_000, suffixes.thousand);
  }

  // Below the smallest compacted unit: show in full with thousands separators.
  // Rounding still removes float artifacts (e.g. 29.9900000002).
  return `${sign}${round(abs).toLocaleString('en-US', { maximumFractionDigits: Math.max(0, decimals) })}`;
}
